import type { LookupOption, PersonEmail, PersonEmailInput } from '@stfors/shared-types';
import './ContactListInput.css';

// Multi-email editor for a person's own emails. First row is implicitly
// primary; the API marks emails[0].is_primary on save.
export interface EmailEntry {
  label: string;
  email: string;
}

/** Edit rows → API payload: drop blank emails, null out blank labels. */
export function emailsToPayload(rows: EmailEntry[]): PersonEmailInput[] {
  return rows
    .map((r) => ({ label: r.label.trim(), email: r.email.trim() }))
    .filter((r) => r.email.length > 0)
    .map((r) => ({ label: r.label || null, email: r.email }));
}

/** Loaded person emails → editable rows. */
export function emailsFromPerson(emails: PersonEmail[]): EmailEntry[] {
  return emails.map((e) => ({ label: e.label ?? '', email: e.email }));
}

interface EmailListInputProps {
  value: EmailEntry[];
  onChange: (next: EmailEntry[]) => void;
  /** Admin-managed email-type labels (lookup_options category 'email_type'). */
  typeOptions: LookupOption[];
  disabled?: boolean;
}

export function EmailListInput({ value, onChange, typeOptions, disabled = false }: EmailListInputProps): JSX.Element {
  const setRow = (idx: number, patch: Partial<EmailEntry>) =>
    onChange(value.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  const removeRow = (idx: number) => onChange(value.filter((_, i) => i !== idx));
  const addRow = () => onChange([...value, { label: '', email: '' }]);
  // Primary = the first row (the API flags emails[0].is_primary on save).
  const makePrimary = (idx: number) => {
    if (idx === 0) return;
    const next = [...value];
    const [row] = next.splice(idx, 1);
    next.unshift(row!);
    onChange(next);
  };

  return (
    <div className="contact-list">
      {value.length === 0 && <p className="contact-list-empty">No emails yet.</p>}
      {value.map((row, idx) => (
        <div key={idx} className="contact-list-row">
          <select
            className="form-input contact-list-label"
            value={row.label}
            onChange={(e) => setRow(idx, { label: e.target.value })}
            disabled={disabled}
            aria-label={`Email ${idx + 1} type`}
          >
            <option value="">—</option>
            {row.label && !typeOptions.some((o) => o.value === row.label) && (
              <option value={row.label}>{row.label} (legacy)</option>
            )}
            {typeOptions.map((o) => <option key={o.id} value={o.value}>{o.value}</option>)}
          </select>
          <input
            className="form-input contact-list-grow"
            type="email"
            placeholder="name@example.com"
            value={row.email}
            onChange={(e) => setRow(idx, { email: e.target.value })}
            disabled={disabled}
            maxLength={254}
            aria-label={`Email ${idx + 1}`}
          />
          {value.length > 1 && (idx === 0 ? (
            <span className="contact-list-primary" title="Primary email">Primary</span>
          ) : (
            <button
              type="button"
              className="contact-list-make-primary"
              onClick={() => makePrimary(idx)}
              disabled={disabled}
              title="Make this the primary email"
            >
              Make primary
            </button>
          ))}
          <button
            type="button"
            className="contact-list-remove"
            onClick={() => removeRow(idx)}
            disabled={disabled}
            aria-label={`Remove email ${idx + 1}`}
          >
            ×
          </button>
        </div>
      ))}
      <button type="button" className="contact-list-add" onClick={addRow} disabled={disabled}>
        + Add email
      </button>
    </div>
  );
}
